/* global google */
import { useEffect, useState, useRef, useCallback } from 'react'
import { createUseStyles } from 'react-jss'
import { useHistory } from 'react-router-dom'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { useJsApiLoader } from '@react-google-maps/api'
import classNames from 'classnames'
import BtnLink from '@/components/BtnLink'
import actions from '@/actions'
import constants from '@/constants'
import Input from './Input'
import style from './style'

const useStyles = createUseStyles(style)

const libraries = ['places'] // To keep out of component because of performance

const FormBoxSearch = ({
  homepage,
  searchTerm,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const $input = useRef()
  const [googlemaps, setGooglemaps] = useState(null)
  const [isGeolocationActive, setGeolocation] = useState(false)
  const [address, setAddress] = useState('')
  const [disableGeoButton, setDisableGeoButton] = useState(false)
  const [currentPosition, setCurrentPosition] = useState([])
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: constants.config.GOOGLE_MAP_API_KEY,
    libraries, // Mandatory library "places" to work with geocoding
  })

  /*------------------------------
  Redux Store
  ------------------------------*/
  const { mediaQueryKey, strings, isSearching } = useSelector((state) => ({
    mediaQueryKey: state.mediaQuery.key,
    strings: state.language.string,
    currentStores: state.stores.results,
    isSearching: state.stores.isSearching,
    resultVisible: state.stores.resultVisible,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const setStateSearchingSL = useCallback((bool) => dispatch(actions.stores.setStateSearchingSL(bool)), [dispatch])
  const setResetResult = useCallback((bool) => dispatch(actions.stores.setResetResult(bool)), [dispatch])
  const setCurrentGeolocation = useCallback((data) => dispatch(actions.stores.setCurrentGeolocation(data)), [dispatch])

  useEffect(() => {
    // if google maps is loaded set the object methods on `googlemaps`
    if (isLoaded && google) setGooglemaps(google.maps)
  }, [isLoaded])

  const handleClickGeolocation = (ev) => {
    ev.preventDefault()
    if (address !== '') {
      $input.current.ref.value = address
    } else {
      setStateSearchingSL(true)
      if ('geolocation' in navigator) {
        const geocoder = new googlemaps.Geocoder()
        navigator.geolocation.getCurrentPosition((position) => {
          geocoder.geocode({ location: { lat: position.coords.latitude, lng: position.coords.longitude } }, (results, status) => {
            if (status === 'OK') {
              setAddress(results[0].formatted_address)
              setStateSearchingSL(false)
            }
          })
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setDisableGeoButton(true)
            setGeolocation(false)
            setStateSearchingSL(false)
          }
        })
      }
    }
  }

  const handleSubmitForm = () => {
    if (homepage) {
      history.push({
        pathname: strings.storefinder_link,
        search: `?q=${btoa($input.current.ref.value)}`,
      })
    } else {
      setResetResult(true)
      setStateSearchingSL(true)
      const place = $input.current.ref.value
      const geocoder = new googlemaps.Geocoder()
      geocoder.geocode({ address: place }, (results, status) => {
        if (status === 'OK') {
          setCurrentPosition(
            [
              results[0].geometry.location.lat(),
              results[0].geometry.location.lng(),
            ],
          )
          setStateSearchingSL(false)
        } else {
          setStateSearchingSL(false)
        }
      })
    }
  }

  useEffect(() => {
    if (currentPosition.length > 0) {
      setCurrentGeolocation([currentPosition[0], currentPosition[1]])
    }
  }, [currentPosition])

  useEffect(() => {
    if (isGeolocationActive && googlemaps) {
      const geocoder = new googlemaps.Geocoder()
      navigator.geolocation.getCurrentPosition((position) => {
        geocoder.geocode({ location: { lat: position.coords.latitude, lng: position.coords.longitude } }, (results, status) => {
          if (status === 'OK') {
            setAddress(results[0].formatted_address)
            setStateSearchingSL(false)
          }
        })
      })
    }
  }, [isGeolocationActive, googlemaps])

  useEffect(() => {
    if (searchTerm && googlemaps) {
      $input.current.ref.value = searchTerm
      handleSubmitForm()
    }
  }, [searchTerm, googlemaps])

  // Focus on Input
  useEffect(() => {
    if ($input.current.ref !== null) $input.current.ref.focus()
  }, [])

  return !loadError && (
    <div className={classes.root}>
      <div
        className={classNames({
          [classes.formBox]: true,
          Hero__formBox: true,
        })}
      >
        <BtnLink
          className={classNames({
            [classes.localizer]: true,
            [classes.disabledButton]: disableGeoButton,
          })}
          onClick={handleClickGeolocation}
          theme="light"
          icon="localizer"
        />
        <span className={classes.border} />
        <Input
          defaultGeolocationAddress={address}
          placeholder={strings.placeholder_label}
          googlemaps={googlemaps}
          isLoading={isSearching}
          ref={$input}
          onSubmit={handleSubmitForm}
        />
        <BtnLink
          className={classes.search}
          onClick={handleSubmitForm}
          {...{ // eslint-disable-line
            ...(mediaQueryKey > 1 && {
              label: strings.search_label,
            }),
          }}
          theme="orange"
          icon="search"
          fill
        />
      </div>
      {!homepage && (
        <div
          className={classes.suggestLine}
          dangerouslySetInnerHTML={{ __html: strings.subline_label }}
        />
      )}
    </div>
  )
}

FormBoxSearch.defaultProps = {
  homepage: false,
  shrink: false,
  searchTerm: '',
}

export default FormBoxSearch
